import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { InventoryUpdateInput } from '@ncc/shared';
import { InventoryForm } from '../components/InventoryForm';
import { api } from '../lib/api';

export async function editLoader({
  params,
  context,
}: {
  params: { id: string };
  context: { queryClient: QueryClient };
}) {
  return context.queryClient.ensureQueryData({
    queryKey: ['inventory', 'detail', Number(params.id)],
    queryFn: () => api.getInventory(Number(params.id)),
  });
}

export function EditListing() {
  const { id } = useParams({ from: '/edit/$id' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPreview, setShowPreview] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const { data: inventory } = useQuery({
    queryKey: ['inventory', 'detail', Number(id)],
    queryFn: () => api.getInventory(Number(id)),
  });

  const save = useMutation({
    mutationFn: async ({
      patch,
      images,
    }: {
      patch: InventoryUpdateInput;
      images: { url: string; image_id?: string | null; position?: number }[];
    }) => {
      // deferSync: the replaceImages call right after mirrors the final state.
      // Letting both mirror races them into a duplicate sheet row.
      const updated = await api.updateInventory(Number(id), patch, { deferSync: true });
      const finalState = await api.replaceImages(Number(id), images);
      return finalState ?? updated;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['inventory', 'detail', updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setShowSaved(true);
    },
  });

  // Confirm the write landed, then get out of the way. Without this the page
  // looked identical whether a save succeeded, failed, or never fired.
  useEffect(() => {
    if (!showSaved) return;
    const t = setTimeout(() => setShowSaved(false), 2500);
    return () => clearTimeout(t);
  }, [showSaved]);

  const remove = useMutation({
    mutationFn: () => api.deleteInventory(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      navigate({ to: '/' });
    },
  });

  if (!inventory) return <div>Loading…</div>;

  return (
    <>
      <header className="toolbar">
        <h2 style={{ margin: 0 }}>
          <span className={`badge ${inventory.status}`} style={{ marginRight: 8 }}>
            {inventory.status}
          </span>
          {inventory.year} {inventory.brand_name ?? ''} {inventory.vehicle_name}
        </h2>
        <span className="grow" />
        <button onClick={() => setShowPreview((v) => !v)}>
          {showPreview ? 'Hide preview' : 'Preview'}
        </button>
        <button
          className="danger"
          onClick={() => {
            if (
              confirm(
                `Delete inventory "${inventory.vehicle_name}" (${inventory.inventory_id})? This cannot be undone.`,
              )
            ) {
              remove.mutate();
            }
          }}
        >
          Delete
        </button>
        <Link to="/">
          <button className="ghost">Back</button>
        </Link>
      </header>

      <div className="card">
        <InventoryForm
          initial={inventory}
          submitLabel="Save changes"
          onSubmit={async (_values, images, changed) => {
            await save.mutateAsync({
              patch: changed as InventoryUpdateInput,
              images,
            });
          }}
        />
      </div>

      {save.isPending ? <div className="toast">Saving…</div> : null}
      {save.isError ? (
        <div className="toast" role="alert">
          Save failed: {save.error instanceof Error ? save.error.message : String(save.error)}
        </div>
      ) : null}
      {showSaved ? <div className="toast">Saved</div> : null}

      {showPreview ? (
        <div
          className="card"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            width: 320,
            maxHeight: '70vh',
            overflow: 'auto',
            boxShadow: 'var(--shadow)',
          }}
        >
          <strong>Preview</strong>
          <div
            style={{
              marginTop: 8,
              aspectRatio: '16 / 10',
              borderRadius: 'var(--radius)',
              background: `var(--surface-2) url(${inventory.images[0]?.url ?? ''}) center / cover no-repeat`,
            }}
          />
          <h3 style={{ margin: '0.5rem 0' }}>
            {inventory.year} {inventory.brand_name} {inventory.vehicle_name}
          </h3>
          <div style={{ color: 'var(--muted)' }}>{inventory.inventory_id}</div>
          <p style={{ marginTop: 8 }}>
            {inventory.description.slice(0, 200)}
            {inventory.description.length > 200 ? '…' : ''}
          </p>
        </div>
      ) : null}
    </>
  );
}
